import { CdkScrollable } from '@angular/cdk/scrolling';
import {
  AsyncPipe,
  CurrencyPipe,
  NgClass,
  NgFor,
  NgIf,
  TitleCasePipe
} from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { FlexModule } from '@angular/flex-layout';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { MatAutocomplete } from '@angular/material/autocomplete';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import {
  ActivatedRoute,
  NavigationStart,
  Router,
  RouterLink
} from '@angular/router';
import { MomentModule } from 'ngx-moment';
import {
  BehaviorSubject,
  EMPTY,
  Subject,
  timer as observableTimer
} from 'rxjs';
import {
  filter,
  map,
  mergeMap,
  shareReplay,
  take,
  takeUntil,
  tap,
  distinctUntilChanged
} from 'rxjs/operators';
import { BOOKING } from 'src/testIds';
import { DepotLocation, Item, Product, User } from '../models';
import { Booking } from '../models/booking';
import { BookingItem } from '../models/booking-item';
import { BookingStatus } from '../models/booking-status';
import { ProductCategory } from '../models/product-category';
import { ConfirmDialogComponent, ItemService, ProductService } from '../shared';
import { BookingStatusComponent } from '../shared/bookings/booking-status.component';
import { CommentsAndEmailsComponent } from '../shared/comments-and-emails/comments-and-emails.component';
import { CommentsComponent } from '../shared/comments/comments.component';
import { DocumentContactComponent } from '../shared/contact-management/document-contact/document-contact.component';
import { DeliveryListItem } from '../shared/deliveries/docket-delivery-list/deliverable-service.interface';
import {
  DELIVERABLE_SERVICE,
  DocketDeliveryListComponent
} from '../shared/deliveries/docket-delivery-list/docket-delivery-list.component';
import { BookingConflictsDialogComponent } from '../shared/dialogs/booking-conflicts-dialog/booking-conflicts-dialog.component';
import { EmailDocumentToCustomerDialogComponent } from '../shared/dialogs/email-document-to-customer-dialog/email-document-to-customer-dialog.component';
import { BookingItemPriceUpdateService } from '../shared/dialogs/hire-price-structure-dialog/booking-item-price-update.service';
import {
  HirePriceStructureDialogComponent,
  HirePriceStructureDialogData,
  PriceStructureDialogResponse
} from '../shared/dialogs/hire-price-structure-dialog/hire-price-structure-dialog.component';
import { HIREABLE_PRICE_UPDATE_IJ } from '../shared/dialogs/hire-price-structure-dialog/hireable-price-update';
import { ItemDialogComponent } from '../shared/dialogs/item-dialog/item-dialog.component';
import { TestIdDirective } from '../shared/directives/test-id.directive';
import { KeyValuePairComponent } from '../shared/general/key-value-pair/key-value-pair.component';
import { base64ToBlob } from '../shared/helpers/base64ToBlob';
import { IntegrationRelationshipInfoComponent } from '../shared/integration-relationship-info/integration-relationship-info.component';
import { ItemSearchComponent } from '../shared/item-search/item-search.component';
import { BookingItemNamePipe } from '../shared/pipes/booking-item-name.pipe';
import { CustomerAddressPipe } from '../shared/pipes/customer-address.pipe';
import { CustomerContactDetailsPipe } from '../shared/pipes/customer-contact-details.pipe';
import { CustomerNamePipe } from '../shared/pipes/customer-name.pipe';
import { DeliveryItemNamePipe } from '../shared/pipes/delivery-item-name.pipe';
import { LocationNameFromIdPipe } from '../shared/pipes/location-name-from-id.pipe';
import { PriceEstimateBreakdownPipe } from '../shared/pipes/price-estimate-breakdown.pipe';
import { PriceContainerComponent } from '../shared/price-container/price-container.component';
import { RetailItemListComponent } from '../shared/retail-item-list/retail-item-list.component';
import {
  RetailItemStoreService,
  SALE_ITEM_SERVICE
} from '../shared/retail-item-list/retail-item.store.service';
import { BookingItemService } from '../shared/services/booking-item.service';
import { BookingRetailItemSaleService } from '../shared/services/booking-retail-item-sale.service';
import { BookingService } from '../shared/services/booking.service';
import { ConfigService } from '../shared/services/config.service';
import { ContactService } from '../shared/services/contact.service';
import { SignatureSectionComponent } from '../shared/signature-section/signature-section.component';
import { UpdateCustomerDialogComponent } from '../shared/update-customer-dialog/update-customer-dialog.component';
import { EditBookingItemDialogComponent } from './edit-booking-item-dialog/edit-booking-item-dialog.component';
import { EditBookingPoNumberDialogComponent } from './edit-booking-po-number/edit-booking-po-number-dialog.component';
import { PriceWrapperComponent } from './price-wrapper/price-wrapper.component';
import { SendQuotationDialogComponent } from './send-quotation-dialog/send-quotation-dialog.component';
import { TransferToContractDialogComponent } from './transfer-to-contract-dialog/transfer-to-contract-dialog.component';
@Component({
  selector: 'app-docket',
  templateUrl: './booking.component.html',
  styleUrls: ['./booking.component.scss'],
  providers: [
    ProductService,
    BookingService,
    BookingItemService,
    ItemService,
    {
      provide: HIREABLE_PRICE_UPDATE_IJ,
      useClass: BookingItemPriceUpdateService
    },
    { provide: SALE_ITEM_SERVICE, useClass: BookingRetailItemSaleService },
    { provide: DELIVERABLE_SERVICE, useClass: BookingService },
    RetailItemStoreService,
    BookingItemNamePipe,
    DeliveryItemNamePipe,
    ContactService
  ],
  standalone: true,
  imports: [
    FlexModule,
    NgIf,
    MatCard,
    MatCardContent,
    BookingStatusComponent,
    MatIconButton,
    MatTooltip,
    MatIcon,
    RouterLink,
    DocumentContactComponent,
    IntegrationRelationshipInfoComponent,
    MatButton,
    MatMenuTrigger,
    TestIdDirective,
    MatMenu,
    MatMenuItem,
    KeyValuePairComponent,
    MatCardTitle,
    NgFor,
    NgClass,
    ExtendedModule,
    ItemSearchComponent,
    SignatureSectionComponent,
    DocketDeliveryListComponent,
    CommentsComponent,
    AsyncPipe,
    TitleCasePipe,
    CurrencyPipe,
    CustomerNamePipe,
    CustomerContactDetailsPipe,
    CustomerAddressPipe,
    LocationNameFromIdPipe,
    PriceEstimateBreakdownPipe,
    MomentModule,
    RetailItemListComponent,
    PriceWrapperComponent,
    CdkScrollable,
    CommentsAndEmailsComponent
  ]
})
export class BookingComponent implements OnInit, OnDestroy {
  booking: Booking;
  products: Array<Product>;
  deleteProcessing = false;
  currentLocation: DepotLocation;
  currentUser: User;
  @ViewChild('printFrame', { static: true }) printFrame;
  @ViewChild(PriceContainerComponent) priceContainer;
  @ViewChild(MatAutocomplete, { static: false }) auto;
  @ViewChild(MatAutocomplete, { static: false }) consumableAutoComplete;
  private ngUnsubscribe = new Subject<void>();
  @ViewChild(ItemSearchComponent, { static: false })
  itemSearch: ItemSearchComponent;
  dateTimeFormat = ConfigService.dates.longDateTimeFormat;
  currency = 'GBP';
  availableStatuses = BookingStatus;
  testIds = BOOKING;
  emailListSource$ = this.route.params.pipe(
    map((params) => ({
      resource: 'booking',
      id: Number(params['bookingId'])
    }))
  );

  private currentBookingId = new BehaviorSubject<null | number>(null);
  items$ = this.currentBookingId.pipe(
    filter((id) => !!id),
    mergeMap((id) => this.bookingItemService.getAll(id)),
    tap((items) => (this.hasItems = items.length > 0)),
    shareReplay(1)
  );
  hasItems = false;
  refreshDataDependentOnHireItems$ = this.items$.pipe(
    map((items) => items.length),
    distinctUntilChanged(),
    map(() => void 0)
  );

  deliveryListItems = new BehaviorSubject<DeliveryListItem[]>([]);
  deliveryListItems$ = this.deliveryListItems.asObservable();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private bookingService: BookingService,
    private bookingItemService: BookingItemService,
    private dialog: MatDialog,
    private viewContainerRef: ViewContainerRef,
    private customerNamePipe: CustomerNamePipe,
    private configService: ConfigService,
    private itemService: ItemService
  ) {}

  ngOnInit() {
    this.route.params
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((params) => {
        let id = params['bookingId'];
        this.currentBookingId.next(id);
        this.getBooking(id);
      });

    this.configService.currentLocation
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((location) => {
        this.currentLocation = location;
      });
    this.configService.currentUser
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((user) => {
        this.currentUser = user;
      });
    this.configService.userConfig.subscribe((config) => {
      if ('currency' in config) {
        this.currency = config['currency'];
      }
    });

    this.router.events
      .pipe(
        filter((event: any) => event instanceof NavigationStart),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.dialog.closeAll();
      });
  }

  private addItemFromQueryParams() {
    this.itemService
      .get(this.getItemIdFromQuery(), ['product.category'])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (item) =>
          this.saveItem(item, item.product, item.product?.category)
      });
  }

  private saveItem(item: Item, product: Product, category?: ProductCategory) {
    const bookingItem: Partial<BookingItem> = {
      user_id: this.currentUser.id,
      item_id: item.id,
      product_id: product.id,
      category_id: category ? category.id : null,
      quantity: 1,
      location_id: this.currentLocation.id,
      item,
      product,
      category
    };
    this.productSelected(bookingItem);
  }

  getBooking(id) {
    this.bookingService
      .getBooking(id, ['customer', 'comments'])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((response) => {
        this.booking = response;
        if (this.getItemIdFromQuery()) {
          this.addItemFromQueryParams();
        }
      });
  }

  changeCustomer() {
    this.dialog
      .open(UpdateCustomerDialogComponent, {
        data: {
          customer: this.booking.customer,
          docketType: 'booking'
        }
      })
      .afterClosed()
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter(Boolean),
        mergeMap((customer) => {
          return this.bookingService.update(this.booking.id, {
            customer_id: customer.id
          });
        })
      )
      .subscribe(() => this.getBooking(this.booking.id));
  }

  private getItemIdFromQuery() {
    return this.route.snapshot.queryParams['addItemId'];
  }

  showItemDialog(item: Item) {
    this.dialog.open(ItemDialogComponent, {
      data: { item }
    });
  }

  productSelected(bookingItem: Partial<BookingItem>) {
    bookingItem.booking_id = this.booking.id;
    if (
      this.itemSearch.quantity > 1 &&
      (!bookingItem.item || bookingItem.item['quantifiable'])
    ) {
      bookingItem.quantity = this.itemSearch.quantity;
    }

    this.items$
      .pipe(
        take(1),
        mergeMap((items) => {
          if (items.length) {
            const { off_hire, on_hire } = items.pop();
            bookingItem.on_hire = on_hire;
            bookingItem.off_hire = off_hire;
          } else {
            bookingItem.on_hire = this.booking.on_hire;
            bookingItem.off_hire = this.booking.off_hire;
          }
          return this.dialog
            .open(EditBookingItemDialogComponent, {
              data: { item: bookingItem, customerId: this.booking.customer_id },
              closeOnNavigation: true,
              disableClose: true
            })
            .afterClosed();
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((added) => {
        this.refreshItems();
        if (this.getItemIdFromQuery()) {
          this.router.navigate([], {
            queryParams: { addItemId: undefined },
            queryParamsHandling: 'merge'
          });
        }
      });
  }

  deleteHireItem(item: BookingItem) {
    let config = new MatDialogConfig();
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, config);
    confirmDialogRef.componentInstance.title = 'Delete Booking Item';
    confirmDialogRef.componentInstance.message =
      "Are you sure you want to delete this booking item?\n\nDeleted booking items are also removed from this booking's delivery and collection jobs.";
    confirmDialogRef.componentInstance.type = 'delete';
    confirmDialogRef
      .afterClosed()
      .pipe(
        takeUntil(this.ngUnsubscribe),
        mergeMap((confirmed) => {
          if (confirmed) {
            this.deleteProcessing = true;
            return this.bookingItemService.delete(this.booking.id, item.id);
          }
          return EMPTY;
        })
      )
      .subscribe({
        next: (success) => {
          this.refreshItems();
          this.deleteProcessing = false;
          this.getBooking(this.booking.id);
        },
        error: (error) => {
          this.deleteProcessing = false;
          this.refreshItems();
        }
      });
  }

  email() {
    this.dialog.open(EmailDocumentToCustomerDialogComponent, {
      data: {
        documentType: 'booking',
        documentId: this.booking.id,
        docket: this.booking,
        customer: this.booking.customer
      },
      viewContainerRef: this.viewContainerRef
    });
  }

  private refreshItems() {
    this.currentBookingId.next(this.currentBookingId.value);
  }

  showEditBookingItemDialog(item: BookingItem) {
    if (this.deleteProcessing) {
      return;
    }
    if (item.category_id === null) {
      item.category = <any>{ name: 'Uncategorised', id: -1 };
    }
    this.dialog
      .open(EditBookingItemDialogComponent, {
        data: { item: item, customerId: this.booking.customer_id },
        panelClass: ['w-full', 'md:w-3/4', 'lg:w-2/3', 'xl:w-1/2'],
        closeOnNavigation: true,
        disableClose: true
      })
      .afterClosed()
      .pipe(
        mergeMap(() => {
          return this.bookingItemService.get(item.id, item.booking_id);
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((bookingItem) => {
        item = Object.assign(item, bookingItem);
        this.refreshItems();
      });
  }

  openConflicts(itemId: number, isVirtualProduct: boolean) {
    this.dialog.open(BookingConflictsDialogComponent, {
      data: {
        type: 'bookingItem',
        id: itemId,
        bookingId: this.booking.id,
        bookingItemId: itemId,
        virtualProduct: isVirtualProduct
      }
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  openConvertDialog() {
    this.items$.pipe(take(1)).subscribe((items) => {
      const itemsIncludingChildItems = items.flatMap((item) => {
        if (item.children.length) {
          // return the item and all its children for each quantity of the parent item
          return Array.from({ length: item.quantity }, () => item).flatMap(
            ({ children }) => children
          );
        }
        return [item];
      });
      this.dialog.open(TransferToContractDialogComponent, {
        data: { ...this.booking, items: itemsIncludingChildItems }
      });
    });
  }

  goToDocket() {
    this.router.navigate(['docket', this.booking.docket_id]);
  }

  updateDeliveries() {
    // TODO: update deliveries
  }

  openReplyDialog() {
    this.items$
      .pipe(
        take(1),
        mergeMap((items) => {
          return this.dialog
            .open(SendQuotationDialogComponent, {
              data: { ...this.booking, items },
              width: '800px',
              maxWidth: '90vw'
            })
            .afterClosed();
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((sentQuote) => {
        if (sentQuote) {
          this.booking.status = BookingStatus.QUOTATION;
        }
      });
  }

  updateToRequest() {
    this.updateBookingStatus({
      status: BookingStatus.REQUEST,
      deleted_at: null
    });
  }

  updateAccepted() {
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent);
    confirmDialogRef.componentInstance.title = 'Mark Booking Accepted';
    confirmDialogRef.componentInstance.message =
      'Are you sure you want to mark this booking as Customer Accepted?';
    confirmDialogRef.componentInstance.type = 'affirmation';
    confirmDialogRef
      .afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.updateBookingStatus({
            status: BookingStatus.CUSTOMER_ACCEPTED,
            deleted_at: null
          });
        }
      });
  }

  updateCancelled() {
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent);
    confirmDialogRef.componentInstance.title = 'Cancel Booking';
    confirmDialogRef.componentInstance.message =
      'Are you sure you want to cancel this booking?';
    confirmDialogRef.componentInstance.type = 'affirmation';
    confirmDialogRef
      .afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.bookingService
            .delete(this.booking.id)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
              this.booking.status = BookingStatus.CANCELLED;
            });
        }
      });
  }

  updateBookingContact(contactId: number | null) {
    this.bookingService
      .update(this.booking.id, { contact_id: contactId })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe();
  }

  openPriceSettings(item: BookingItem) {
    this.dialog
      .open<
        HirePriceStructureDialogComponent,
        HirePriceStructureDialogData,
        PriceStructureDialogResponse
      >(HirePriceStructureDialogComponent, {
        data: {
          productId: item?.product_id,
          customerId: this.booking.customer_id,
          parentId: item.booking_id,
          currencyRate: 1,
          quantity: item.quantity,
          hireableId: item.id,
          hireableType: 'bookingItem',
          onHire: item.on_hire.toString(),
          offHire: item.off_hire.toString(),
          currency: this.currency,
          itemIsOffHired: true,
          fixedPrice: item.pricing_type === 'fixed' ? item.charge : null,
          free_accessory: item.free_accessory,
          zero_price_reason: item.zero_price_reason,
          pricingType: item.pricing_type as
            | 'default'
            | 'fixed'
            | 'custom'
            | 'no_charge'
        },
        viewContainerRef: this.viewContainerRef
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.currentBookingId.next(item.booking_id);
      });
  }

  private updateBookingStatus(update: {
    status: BookingStatus;
    deleted_at?: null;
  }) {
    this.bookingService
      .update(this.booking.id, <any>update)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.booking.status = update.status;
      });
  }

  print(download = false) {
    this.bookingService
      .print(this.booking.id)
      .pipe(takeUntil(this.ngUnsubscribe), takeUntil(this.ngUnsubscribe))
      .subscribe((response) => {
        let src = URL.createObjectURL(
          base64ToBlob(response, 'application/pdf')
        );
        if (download) {
          const link: HTMLAnchorElement = document.createElement(
            'a'
          ) as HTMLAnchorElement;
          link.href = src;
          link.download =
            this.booking.id +
            ' (' +
            this.customerNamePipe.transform(this.booking.customer) +
            ')';
          link.click();
        } else {
          this.printFrame.nativeElement.src = src;
          this.printIframe();
        }
      });
  }

  printIframe() {
    observableTimer(500)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.printFrame.nativeElement.contentWindow.print();
      });
  }

  editPo() {
    this.dialog
      .open(EditBookingPoNumberDialogComponent, <MatDialogConfig>{
        data: {
          purchase_order: this.booking.purchase_order,
          bookingId: this.booking.id
        },
        viewContainerRef: this.viewContainerRef
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((data) => !!data),
        tap((data) => {
          this.booking.purchase_order = data.purchase_order;
        })
      )
      .subscribe();
  }
}
